import { Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toGreekCaps } from '~/lib/i18n/toGreekCaps';

export interface Tier {
	id: string;
	threshold: number;
	value: number;
}

interface TierTableProps {
	tiers: Tier[];
	onChange: (next: Tier[]) => void;
	/** Threshold column meaning: cart amount (€) or quantity (items). */
	mode: 'amount' | 'quantity';
	/** Value column meaning: percent (%), amount (€) or fixed price (€). */
	valueLabel: 'percent' | 'amount' | 'price';
	/** Single sticky tier with no threshold column. */
	hideThreshold?: boolean;
	addLabel?: string;
}

function thresholdHeader(mode: TierTableProps['mode']): string {
	return mode === 'amount' ? 'Ποσό καλαθιού (€)' : 'Ποσότητα (τεμάχια)';
}

function valueHeader(valueLabel: TierTableProps['valueLabel']): string {
	if (valueLabel === 'percent') return 'Ποσοστό έκπτωσης (%)';
	if (valueLabel === 'price') return 'Τιμή (€)';
	return 'Ποσό (€)';
}

/** Numeric field with a leading (€) or trailing (%) adornment. */
function NumberField({
	value,
	onChange,
	prefix,
	suffix
}: {
	value: number;
	onChange: (n: number) => void;
	prefix?: string;
	suffix?: string;
}) {
	return (
		<div className="relative">
			{prefix && (
				<span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-foreground-subtle">{prefix}</span>
			)}
			<input
				type="number"
				value={Number.isFinite(value) ? value : ''}
				onChange={e => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
				className={cn(
					'w-full rounded-md border border-border bg-surface py-2 text-sm text-foreground',
					'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
					prefix ? 'pl-6 pr-2' : 'px-2.5',
					suffix && 'pr-6'
				)}
			/>
			{suffix && (
				<span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-foreground-subtle">{suffix}</span>
			)}
		</div>
	);
}

/** Tabular editor for tier-mode actions. Order is preserved as entered (not auto-sorted). */
export function TierTable({ tiers, onChange, mode, valueLabel, hideThreshold, addLabel = 'Προσθήκη βαθμίδας' }: TierTableProps) {
	const thresholdPrefix = mode === 'amount' ? '€' : undefined;
	const valuePrefix = valueLabel === 'percent' ? undefined : '€';
	const valueSuffix = valueLabel === 'percent' ? '%' : undefined;

	const update = (id: string, patch: Partial<Tier>) => onChange(tiers.map(t => (t.id === id ? { ...t, ...patch } : t)));
	const removeRow = (id: string) => onChange(tiers.filter(t => t.id !== id));
	const addRow = () => onChange([...tiers, { id: crypto.randomUUID(), threshold: 0, value: 0 }]);

	const colsClass = hideThreshold ? 'grid-cols-[1fr_32px]' : 'grid-cols-[1fr_1fr_32px]';

	return (
		<div className="flex flex-col gap-2">
			<div className={cn('grid items-center gap-2 px-1', colsClass)}>
				{!hideThreshold && (
					<span className="text-[10px] font-semibold tracking-[0.04em] text-foreground-subtle">{toGreekCaps(thresholdHeader(mode))}</span>
				)}
				<span className="text-[10px] font-semibold tracking-[0.04em] text-foreground-subtle">{toGreekCaps(valueHeader(valueLabel))}</span>
				<span />
			</div>

			{tiers.map(tier => (
				<div key={tier.id} className={cn('grid items-center gap-2', colsClass)}>
					{!hideThreshold && (
						<NumberField value={tier.threshold} onChange={n => update(tier.id, { threshold: n })} prefix={thresholdPrefix} />
					)}
					<NumberField value={tier.value} onChange={n => update(tier.id, { value: n })} prefix={valuePrefix} suffix={valueSuffix} />
					<button
						type="button"
						onClick={() => removeRow(tier.id)}
						disabled={hideThreshold && tiers.length <= 1}
						className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-muted hover:bg-muted hover:text-foreground disabled:opacity-30"
						aria-label="remove tier">
						<X size={14} />
					</button>
				</div>
			))}

			{!hideThreshold && (
				<button
					type="button"
					onClick={addRow}
					className="flex items-center justify-center gap-1.5 rounded-md border border-dashed border-border-strong py-2 text-sm text-foreground-muted transition-colors hover:border-brand hover:bg-brand-subtle hover:text-brand-subtle-fg">
					<Plus size={14} /> {addLabel}
				</button>
			)}
		</div>
	);
}
